class MiniPlayerManager {
  public pipWindow: Window | null = null;
  private onCloseCallback: (() => void) | null = null;
  private listeners = new Set<(win: Window | null) => void>();

  public get isOpen(): boolean {
    return this.pipWindow !== null && !this.pipWindow.closed;
  }

  public subscribe(cb: (win: Window | null) => void) {
    this.listeners.add(cb);
    // Immediately emit current state
    cb(this.pipWindow && !this.pipWindow.closed ? this.pipWindow : null);
    return () => {
      this.listeners.delete(cb);
    };
  }

  private notify(win: Window | null) {
    this.listeners.forEach((cb) => {
      try {
        cb(win);
      } catch (_) {}
    });
  }

  public async openWindow(onClose?: () => void): Promise<Window | null> {
    if (onClose) {
      this.onCloseCallback = onClose;
    }

    if (this.isOpen && this.pipWindow) {
      try {
        this.pipWindow.focus();
      } catch (_) {}
      this.notify(this.pipWindow);
      return this.pipWindow;
    }

    // Clean up any stale orphaned documentPictureInPicture window
    if (typeof window !== 'undefined' && 'documentPictureInPicture' in window) {
      const existingPip = (window as any).documentPictureInPicture.window;
      if (existingPip && !existingPip.closed) {
        try {
          existingPip.close();
        } catch (_) {}
      }
    }

    const isElectron =
      typeof navigator !== 'undefined' && /electron/i.test(navigator.userAgent);

    // If running in Electron, use frameless window directly
    if (isElectron) {
      try {
        const left = Math.max(20, window.screen.width - 350);
        const top = Math.max(20, window.screen.height - 440);
        const popup = window.open(
          '',
          'SpotifyMiniPlayer',
          `width=320,height=380,left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no,resizable=yes`
        );
        if (popup) {
          this.pipWindow = popup;
          this.setupWindow(popup);
          this.notify(popup);
          return popup;
        }
      } catch (err) {
        console.warn('Electron popup window request failed:', err);
      }
    }

    // 1. Modern Document Picture-in-Picture API (Chrome 116+, Edge 116+, Brave)
    if (typeof window !== 'undefined' && 'documentPictureInPicture' in window) {
      try {
        const pip = await (window as any).documentPictureInPicture.requestWindow({
          width: 320,
          height: 380,
          disallowReturnToOpener: true,
        });
        this.pipWindow = pip;
        this.setupWindow(pip);
        this.notify(pip);
        return pip;
      } catch (err) {
        console.warn('Document Picture-in-Picture request failed:', err);
      }
    }

    // 2. Native OS popup window fallback
    try {
      const left = Math.max(20, window.screen.width - 350);
      const top = Math.max(20, window.screen.height - 440);
      const popup = window.open(
        '',
        'SpotifyMiniPlayer',
        `width=320,height=380,left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no,resizable=yes`
      );
      if (popup) {
        this.pipWindow = popup;
        this.setupWindow(popup);
        this.notify(popup);
        return popup;
      }
    } catch (err) {
      console.warn('Popup window request failed:', err);
    }

    this.notify(null);
    return null;
  }

  public close() {
    if (this.pipWindow && !this.pipWindow.closed) {
      try {
        this.pipWindow.close();
      } catch (_) {}
    }
    this.pipWindow = null;
    this.notify(null);
    if (this.onCloseCallback) {
      const cb = this.onCloseCallback;
      this.onCloseCallback = null;
      cb();
    }
  }

  private setupWindow(targetWin: Window) {
    const doc = targetWin.document;
    doc.title = 'OpenMusic Miniplayer';

    // Copy all style & link tags from main document
    const copyStyles = () => {
      document.querySelectorAll('link[rel="stylesheet"], style').forEach((node) => {
        try {
          doc.head.appendChild(node.cloneNode(true));
        } catch (_) {}
      });

      // Also copy inline stylesheets if available
      try {
        Array.from(document.styleSheets).forEach((sheet) => {
          try {
            if (sheet.cssRules && sheet.cssRules.length > 0) {
              const style = doc.createElement('style');
              Array.from(sheet.cssRules).forEach((rule) => {
                style.appendChild(doc.createTextNode(rule.cssText));
              });
              doc.head.appendChild(style);
            }
          } catch (_) {
            // Cross-origin sheets handled by <link>
          }
        });
      } catch (_) {}
    };

    copyStyles();

    // Base Reset Styles and Marquee Keyframes for the floating OS window
    const resetEl = doc.createElement('style');
    resetEl.textContent = `
      * {
        box-sizing: border-box !important;
      }
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        width: 100vw !important;
        height: 100vh !important;
        background-color: #121212 !important;
        color: #ffffff !important;
        overflow: hidden !important;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif !important;
        user-select: none !important;
      }
      #miniplayer-portal-root {
        width: 100vw;
        height: 100vh;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }

      /* Marquee Keyframes */
      @keyframes auto-scroll-anim {
        0%, 15% {
          transform: translateX(0%);
        }
        45%, 55% {
          transform: translateX(var(--scroll-dist));
        }
        85%, 100% {
          transform: translateX(0%);
        }
      }
      @keyframes hover-scroll-anim {
        0%, 15% {
          transform: translateX(0%);
        }
        45%, 55% {
          transform: translateX(var(--scroll-dist));
        }
        85%, 100% {
          transform: translateX(0%);
        }
      }
      .marquee-container {
        overflow: hidden;
        white-space: nowrap;
        width: 100%;
      }
      .marquee-container.is-overflowing {
        -webkit-mask-image: linear-gradient(to right, black calc(100% - 14px), transparent 100%);
        mask-image: linear-gradient(to right, black calc(100% - 14px), transparent 100%);
      }
      .marquee-content {
        display: inline-flex;
      }
      .marquee-content.scroll {
        animation: auto-scroll-anim 8s ease-in-out infinite;
        animation-delay: 1.5s;
      }
      .marquee-container:hover .marquee-content.scroll {
        animation: hover-scroll-anim 8s ease-in-out infinite;
        animation-delay: 0s;
      }
    `;
    doc.head.appendChild(resetEl);

    // Create mount root if not present
    let rootEl = doc.getElementById('miniplayer-portal-root');
    if (!rootEl) {
      rootEl = doc.createElement('div');
      rootEl.id = 'miniplayer-portal-root';
      doc.body.appendChild(rootEl);
    }

    // Clean up on window close
    const handleClose = () => {
      this.pipWindow = null;
      this.notify(null);
      if (this.onCloseCallback) {
        const cb = this.onCloseCallback;
        this.onCloseCallback = null;
        cb();
      }
    };

    targetWin.addEventListener('pagehide', handleClose);
    targetWin.addEventListener('beforeunload', handleClose);
  }
}

export const miniPlayerManager = new MiniPlayerManager();
